import * as React from "react";
import {useCallback, useState} from "react";
import {Editor} from "@tiptap/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBold,
    faItalic,
    faListUl,
    faListOl,
    faLink,
    faLinkSlash,
    faHeading,
} from "@fortawesome/pro-regular-svg-icons";
import {useIntl} from "react-intl";

interface RichTextEditorToolbarProps {
    editor: Editor | null;
}

interface ToolbarButtonProps {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({onClick, isActive = false, disabled = false, title, children}) => {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`p-1.5 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive ? "bg-gray-200 text-red-600" : "text-gray-600"
            }`}
        >
            {children}
        </button>
    );
};

const RichTextEditorToolbar: React.FC<RichTextEditorToolbarProps> = ({editor}) => {
    const intl = useIntl();
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    const handleSetLink = useCallback(() => {
        if (!editor) return;

        if (linkUrl === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange("link").setLink({href: linkUrl}).run();
        }
        setShowLinkInput(false);
        setLinkUrl("");
    }, [editor, linkUrl]);

    const handleOpenLinkInput = useCallback(() => {
        if (!editor) return;

        const previousUrl = editor.getAttributes("link").href || "";
        setLinkUrl(previousUrl);
        setShowLinkInput(true);
    }, [editor]);

    const handleRemoveLink = useCallback(() => {
        if (!editor) return;
        editor.chain().focus().unsetLink().run();
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="flex items-center gap-1 flex-wrap">
            {/* Heading Dropdown */}
            <select
                value={
                    editor.isActive("heading", {level: 1})
                        ? "h1"
                        : editor.isActive("heading", {level: 2})
                            ? "h2"
                            : editor.isActive("heading", {level: 3})
                                ? "h3"
                                : "p"
                }
                onChange={(e) => {
                    const value = e.target.value;
                    if (value === "p") {
                        editor.chain().focus().setParagraph().run();
                    } else {
                        const level = parseInt(value.replace("h", "")) as 1 | 2 | 3;
                        editor.chain().focus().toggleHeading({level}).run();
                    }
                }}
                className="h-8 px-2 text-sm border border-gray-300 rounded-sm bg-white focus:outline-hidden focus:ring-1 focus:ring-red-500"
            >
                <option value="p">{intl.formatMessage({id: "richTextEditor.paragraph"})}</option>
                <option value="h1">{intl.formatMessage({id: "richTextEditor.heading1"})}</option>
                <option value="h2">{intl.formatMessage({id: "richTextEditor.heading2"})}</option>
                <option value="h3">{intl.formatMessage({id: "richTextEditor.heading3"})}</option>
            </select>

            <div className="w-px h-6 bg-gray-300 mx-1"/>

            {/* Bold */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive("bold")}
                title={intl.formatMessage({id: "richTextEditor.bold"})}
            >
                <FontAwesomeIcon icon={faBold} className="w-4 h-4"/>
            </ToolbarButton>

            {/* Italic */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive("italic")}
                title={intl.formatMessage({id: "richTextEditor.italic"})}
            >
                <FontAwesomeIcon icon={faItalic} className="w-4 h-4"/>
            </ToolbarButton>

            <div className="w-px h-6 bg-gray-300 mx-1"/>

            {/* Bullet List */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive("bulletList")}
                title={intl.formatMessage({id: "richTextEditor.bulletList"})}
            >
                <FontAwesomeIcon icon={faListUl} className="w-4 h-4"/>
            </ToolbarButton>

            {/* Ordered List */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive("orderedList")}
                title={intl.formatMessage({id: "richTextEditor.orderedList"})}
            >
                <FontAwesomeIcon icon={faListOl} className="w-4 h-4"/>
            </ToolbarButton>

            <div className="w-px h-6 bg-gray-300 mx-1"/>

            {/* Link */}
            {showLinkInput ? (
                <div className="flex items-center gap-1">
                    <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://..."
                        className="h-7 px-2 text-sm border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-red-500"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSetLink();
                            }
                            if (e.key === "Escape") {
                                setShowLinkInput(false);
                                setLinkUrl("");
                            }
                        }}
                        autoFocus
                    />
                    <button
                        type="button"
                        onClick={handleSetLink}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded-sm hover:bg-red-700"
                    >
                        {intl.formatMessage({id: "richTextEditor.insertLink"})}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setShowLinkInput(false);
                            setLinkUrl("");
                        }}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                    >
                        ×
                    </button>
                </div>
            ) : (
                <>
                    <ToolbarButton
                        onClick={handleOpenLinkInput}
                        isActive={editor.isActive("link")}
                        title={intl.formatMessage({id: "richTextEditor.addLink"})}
                    >
                        <FontAwesomeIcon icon={faLink} className="w-4 h-4"/>
                    </ToolbarButton>

                    {editor.isActive("link") && (
                        <ToolbarButton
                            onClick={handleRemoveLink}
                            title={intl.formatMessage({id: "richTextEditor.removeLink"})}
                        >
                            <FontAwesomeIcon icon={faLinkSlash} className="w-4 h-4"/>
                        </ToolbarButton>
                    )}
                </>
            )}
        </div>
    );
};

export default RichTextEditorToolbar;
